import _ from "lodash";
import moment from "moment";
import { Fragment, useCallback, useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { BinIcon } from "../../../../components/icon/Icon.jsx";
import { useResponsive } from "../../../../components/responsive";
import { useLangContext } from "../../../../providers/LangProvider/LangProviderContext.js";
import amountFormat from "../../../../utils/amountFormat";
import {
    CashierContentContainerStyled,
    TransactionContentLeftStyled,
    TransactionContentRightStyled,
    TransactionInformationStyled,
    TransactionItemAmountStyled,
    TransactionItemContentStyled,
    TransactionItemDateStyled,
    TransactionItemDescrStyled,
    TransactionItemIconStyled,
    TransactionItemRowStyled,
    TransactionItemSideStyled,
    TransactionItemStyled,
    TransactionListStyled
} from "../../../Cashier/styled/CashierStyled";
import { useGTMEvent } from "../../../GoogleTagManager/googleTagManagerHooks";
import {
    payoutCreateAction,
    payoutGetAvailableMethodsAction,
    payoutGetFormAction,
    payoutGetTransactionsAction
} from "../../accountActions";
import { makeSelectPayoutAvailableMethods, getTransactionHistory } from "../../accountSelectors";
import { getTransactionIconProps, getTransactionTypeText } from "./transactionHistoryUtils";
import TransactionsInfo from "./TransactionsInfo.jsx";

const stateToIcon = {
    processing: {
        iconName: 'IcClock',
        iconColor: 'var(--color-secondary-active)',
    },
    error: {
        iconName: 'IcXCircleRed',
    },
    rejected: {
        iconName: 'IcXCircleRed',
    },
    completed: {
        iconName: 'IcCheckMarkCircleGreen',
    },
};

function TransactionsHistory(props) {
    const { isMediumMaxWidth } = useResponsive();
    const { getLangPath } = useLangContext();
    const { handleGTMClick } = useGTMEvent({ module: 'Cashier' });
    const {
        setSelectedItem,
        transactionHistory,
        payoutGetTransactions,
    } = props;

    const [transactionSelected, setTransactionSelected] = useState(null);
    const unSelect = useCallback(() => setTransactionSelected(null), []);

    useEffect(() => {
        payoutGetTransactions();
    }, [payoutGetTransactions]);

    const setTransactionSelectedHandler = useCallback((e) => {
        const uuid = e.currentTarget.dataset.uuid;
        const transaction = transactionHistory.find(item => item.uuid === uuid);
        setTransactionSelected(transaction);
        handleGTMClick({
            amount: transaction.amount,
            button: 'transaction item',
            provider: transaction.payment_name,
            side: transaction.side,
            status: transaction.status,
            state: transaction.state,
            transaction_id: transaction.uuid,
            type: transaction.type,
        })
    }, [handleGTMClick, transactionHistory]);

    const handleFirstDepositClick = useCallback(() => {
        setSelectedItem('deposit');
        handleGTMClick({ button: 'deposit first' })
    }, [handleGTMClick, setSelectedItem])

    if (!transactionHistory.length) {
        return <CashierContentContainerStyled>
            <div style={{ display: 'flex' }}>
                <TransactionInformationStyled>
                    <BinIcon name={'IcHarpoonLeftRight'} style={{ fontSize: '56px', color: '#CAD0E7' }}/>
                    <h4>{getLangPath('transaction_history.modal_title')}</h4>
                    <p dangerouslySetInnerHTML={{ __html: getLangPath('transaction_history.modal_text') }}/>
                    <Button variant={'primary'} size={'md'} onClick={handleFirstDepositClick}>
                        {getLangPath('transaction_history.modal_button')}
                    </Button>
                </TransactionInformationStyled>
            </div>
        </CashierContentContainerStyled>
    }

    return <>
        <CashierContentContainerStyled>
            <TransactionContentLeftStyled>

                <TransactionListStyled>
                    {
                        _.sortBy(transactionHistory, 'created_at').reverse().map(item => (<Fragment key={item.uuid}>
                            <TransactionItemStyled data-uuid={item.uuid}
                                                   onClick={setTransactionSelectedHandler}
                                                   className={transactionSelected?.uuid === item.uuid ? 'active' : ''}>
                                <TransactionItemIconStyled>
                                    <BinIcon {...getTransactionIconProps(item)}/>
                                </TransactionItemIconStyled>
                                <TransactionItemContentStyled>
                                    <TransactionItemRowStyled>
                                        <TransactionItemDescrStyled>
                                            {item.payment_name}
                                        </TransactionItemDescrStyled>
                                        <TransactionItemAmountStyled
                                            className={(item.state === 'error' || item.state === 'rejected') ?
                                                'dark' :
                                                ''}>
                                            ${amountFormat(item.amount, 0)}
                                        </TransactionItemAmountStyled>
                                    </TransactionItemRowStyled>
                                    <TransactionItemRowStyled>
                                        <TransactionItemSideStyled>
                                            {getTransactionTypeText({ ...item, getLangPath })}
                                        </TransactionItemSideStyled>
                                        <TransactionItemDateStyled>
                                            {moment(item.created_at).format('HH:mm, DD MMM')}
                                        </TransactionItemDateStyled>
                                    </TransactionItemRowStyled>
                                </TransactionItemContentStyled>
                                <TransactionItemIconStyled>
                                    <BinIcon
                                        name={stateToIcon[item.state]?.iconName}
                                        style={{fill: stateToIcon[item.state]?.iconColor}}
                                    />
                                </TransactionItemIconStyled>
                            </TransactionItemStyled>
                        </Fragment>))
                    }
                </TransactionListStyled>

            </TransactionContentLeftStyled>
            {
                !isMediumMaxWidth &&
                <TransactionContentRightStyled>
                    <h6 className="mb-4">{getLangPath('transaction_history.details')}</h6>
                    {
                        !transactionSelected &&
                        <>
                            <center>
                                <BinIcon name={'IcHarpoonLeftRight'} style={{ fontSize: '56px', color: '#CAD0E7' }}/>
                                <p>{getLangPath('transaction_history.select_transaction')}</p>
                            </center>
                        </>
                    }
                    {
                        transactionSelected && <>
                            <TransactionsInfo
                                stateToIcon={stateToIcon}
                                transactionSelected={transactionSelected}
                            />
                        </>
                    }
                </TransactionContentRightStyled>
            }
        </CashierContentContainerStyled>
        {
            isMediumMaxWidth && transactionSelected && <>
                <Modal show={!!transactionSelected}
                       onHide={unSelect}
                       fullscreen={true}
                       animation={false}>
                    <Modal.Header closeButton>
                        {getLangPath('transaction_history.details')}
                    </Modal.Header>
                    <Modal.Body>
                        <TransactionsInfo
                            stateToIcon={stateToIcon}
                            transactionSelected={transactionSelected}
                        />
                    </Modal.Body>
                </Modal>
            </>
        }
    </>
}

const mapStateToProps = createStructuredSelector({
    payoutAvailableMethods: makeSelectPayoutAvailableMethods(),
    transactionHistory: getTransactionHistory,
});

const mapDispatchToProps = (dispatch) => ({
    payoutCreate: payload => dispatch(payoutCreateAction(payload)),
    payoutGetAvailableMethods: payload => dispatch(payoutGetAvailableMethodsAction(payload)),
    payoutGetForm: payload => dispatch(payoutGetFormAction(payload)),
    payoutGetTransactions: payload => dispatch(payoutGetTransactionsAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TransactionsHistory);
